import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { v4 as uuidv4 } from 'uuid';
import MD5 from 'crypto-js/md5.js';

// Define the structure of a single element in the DOM snapshot
export interface DOMElement {
	selector: string;
	tag: string;
	textContent: string;
	visible: boolean;
	classList: string[];
	attributes: Record<string, string>;
	boundingBox: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	hierarchy: {
		relationship: 'self' | 'parent' | 'sibling' | 'child';
		depth?: number; // Only used for children to track nesting level (0–3)
	};
}

export interface DOMSnapshot {
	id: string;
	timestamp: number;
	domSnapshot: string; // Serialized JSON array of DOMElement
	domSnapshotHash?: string; // Hash of the DOM snapshot for quick comparison
	interactionType: 'click' | 'input' | 'drag-start' | 'drag-end' | 'custom';
	targetElement: DOMElement;
	previousSnapshotId?: string; // Reference to previous snapshot if this one reuses its DOM snapshot
	interactionMetadata?: {
		cursorPosition?: { x: number; y: number };
		inputValue?: string;
		dragStart?: { x: number; y: number };
		dragEnd?: { x: number; y: number };
	};
	viewport?: {
		width: number;
		height: number;
	};
}

// Recording action flow data
export interface ActionFlow {
	id: string;
	startTime: number;
	endTime?: number;
	snapshots: DOMSnapshot[];
	name?: string;
	description?: string;
}

export interface SnapshotSlice {
	// State
	isRecording: boolean;
	currentActionFlow: ActionFlow | null;
	savedActionFlows: ActionFlow[];

	// Actions
	startRecording: (name?: string, description?: string) => void;
	stopRecording: () => ActionFlow;
	captureSnapshot: (
		interactionType: DOMSnapshot['interactionType'],
		targetElement: string | Element, // Element to capture or existing selector
		interactionMetadata?: DOMSnapshot['interactionMetadata'],
		replaceLastSnapshot?: boolean
	) => DOMSnapshot;
	clearCurrentActionFlow: () => void;
	deleteActionFlow: (actionFlowId: string) => void;
	renameActionFlow: (actionFlowId: string, name: string) => void;
	updateActionFlowDescription: (
		actionFlowId: string,
		description: string
	) => void;
	exportActionFlow: (actionFlowId: string) => string; // Returns JSON string
	importActionFlow: (actionFlowData: string) => boolean; // Returns success status
}

// Helper functions for detailed DOM state serialization
const getAttributes = (el: Element): Record<string, string> => {
	const attrs: Record<string, string> = {};
	for (const attr of Array.from(el.attributes || [])) {
		attrs[attr.name] = attr.value;
	}
	return attrs;
};

const getUniqueSelector = (el: Element): string => {
	if (el.id) return `#${el.id}`;
	const path: string[] = [];
	let currentEl: Element | null = el;

	while (
		currentEl &&
		currentEl.nodeType === Node.ELEMENT_NODE &&
		currentEl !== document.body
	) {
		let selector = currentEl.nodeName.toLowerCase();
		if (currentEl.className && typeof currentEl.className === 'string') {
			const classes = currentEl.className
				.toString()
				.split(' ')
				.filter(Boolean)
				.join('.');
			if (classes) selector += `.${classes}`;
		}
		const siblings = Array.from(currentEl.parentNode?.children || []);
		const index = siblings.indexOf(currentEl) + 1;
		selector += `:nth-child(${index})`;
		path.unshift(selector);
		currentEl = currentEl.parentNode as Element;
	}
	return path.join(' > ');
};

const getElementSnapshot = (el: Element): Omit<DOMElement, 'hierarchy'> => {
	const rect = el.getBoundingClientRect();
	const style = window.getComputedStyle(el as HTMLElement);

	return {
		selector: getUniqueSelector(el),
		tag: el.tagName.toLowerCase(),
		textContent: el.textContent?.trim().slice(0, 200) || '',
		visible:
			rect.width > 0 &&
			rect.height > 0 &&
			style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			style.opacity !== '0',
		classList: Array.from(el.classList || []),
		attributes: getAttributes(el),
		boundingBox: {
			x: rect.x,
			y: rect.y,
			width: rect.width,
			height: rect.height,
		},
	};
};
const serializeDOMState = (targetElement: Element): string => {
	const seen = new Set<Element>();
	const result: DOMElement[] = [];

	const addIfVisible = (
		el: Element,
		relationship: DOMElement['hierarchy']['relationship'],
		depth?: number
	) => {
		if (seen.has(el)) return;
		const rect = el.getBoundingClientRect();
		const style = window.getComputedStyle(el as HTMLElement);
		const isVisible =
			rect.width > 0 &&
			rect.height > 0 &&
			style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			style.opacity !== '0';

		if (isVisible) {
			const snapshot = getElementSnapshot(el) as DOMElement;
			snapshot.hierarchy = {
				relationship,
				...(depth !== undefined && { depth }),
			};
			result.push(snapshot);
			seen.add(el);
		}
	};

	// 1. Add the target element
	addIfVisible(targetElement, 'self');

	// 2. Add all parents up to <body>
	let parent = targetElement.parentElement;
	while (parent && parent !== document.body) {
		addIfVisible(parent, 'parent');
		parent = parent.parentElement;
	}

	// 3. Add all siblings of the target element
	const siblings = Array.from(targetElement.parentElement?.children || []);
	siblings.forEach((sib) => {
		if (sib !== targetElement) addIfVisible(sib, 'sibling');
	});

	// 4. Add children (up to 3 levels deep)
	const addChildrenRecursively = (el: Element, depth: number) => {
		if (depth === 0) return;
		const children = Array.from(el.children || []);
		children.forEach((child) => {
			addIfVisible(child, 'child', 3 - depth);
			addChildrenRecursively(child, depth - 1);
		});
	};

	addChildrenRecursively(targetElement, 3);

	return JSON.stringify(result);
};

// Helper function to compute hash of DOM snapshot
const computeSnapshotHash = (domSnapshot: string): string => {
	// Use crypto-js MD5 implementation
	try {
		return MD5(domSnapshot).toString();
	} catch {
		// Fallback if crypto-js is not available in the environment
		// Simple hash function for strings
		let hash = 0;
		for (let i = 0; i < domSnapshot.length; i++) {
			const char = domSnapshot.charCodeAt(i);
			hash = (hash << 5) - hash + char;
			hash = hash & hash; // Convert to 32bit integer
		}
		return hash.toString(16);
	}
};

// Create the slice
export const createSnapshotSlice: StateCreator<
	CedarStore,
	[],
	[],
	SnapshotSlice
> = (set, get) => ({
	// Default state
	isRecording: false,
	currentActionFlow: null,
	savedActionFlows: [],

	// Start a new recording action flow
	startRecording: (name?: string, description?: string) => {
		const currentTime = Date.now();
		const { savedActionFlows } = get();

		// Get the last action flow's name and description if available
		let defaultName = `Action Flow ${new Date(currentTime).toLocaleString()}`;
		let defaultDescription = '';

		if (savedActionFlows.length > 0) {
			const lastActionFlow = savedActionFlows[savedActionFlows.length - 1];
			defaultName = lastActionFlow.name || defaultName;
			defaultDescription = lastActionFlow.description || defaultDescription;
		}

		// Create a new action flow
		const newActionFlow: ActionFlow = {
			id: uuidv4(),
			startTime: currentTime,
			snapshots: [],
			name: name || defaultName,
			description: description || defaultDescription,
		};

		set({
			isRecording: true,
			currentActionFlow: newActionFlow,
		});
	},

	// Stop the current recording action flow and save it
	stopRecording: () => {
		const { currentActionFlow, savedActionFlows } = get();

		if (!currentActionFlow) {
			throw new Error('No active recording action flow to stop');
		}

		// Update the action flow with end time
		const completedActionFlow: ActionFlow = {
			...currentActionFlow,
			endTime: Date.now(),
		};

		set({
			isRecording: false,
			currentActionFlow: null,
			savedActionFlows: [...savedActionFlows, completedActionFlow],
		});

		return completedActionFlow;
	},

	captureSnapshot: (
		interactionType: DOMSnapshot['interactionType'],
		targetElement: string | Element,
		interactionMetadata?: DOMSnapshot['interactionMetadata'],
		replaceLastSnapshot?: boolean
	): DOMSnapshot => {
		const { currentActionFlow, isRecording } = get();

		if (!isRecording || !currentActionFlow) {
			throw new Error('No active recording action flow');
		}

		const enhancedMetadata = { ...interactionMetadata };
		let targetElementSnapshot: DOMElement | null = null;

		if (targetElement instanceof Element) {
			if (
				targetElement instanceof HTMLInputElement ||
				targetElement instanceof HTMLTextAreaElement ||
				targetElement instanceof HTMLSelectElement
			) {
				enhancedMetadata.inputValue = targetElement.value;
			}

			targetElementSnapshot = getElementSnapshot(targetElement) as DOMElement;
		}

		// Serialize the DOM state
		const serializedDOMState = serializeDOMState(targetElement as Element);

		// Compute the hash for this DOM snapshot
		const domSnapshotHash = computeSnapshotHash(serializedDOMState);

		// Check if we can reuse the previous snapshot's DOM data
		let previousSnapshotId: string | undefined = undefined;
		let domSnapshot = serializedDOMState;

		// Only compare with the last snapshot if there is one
		if (currentActionFlow.snapshots.length > 0) {
			const previousSnapshot =
				currentActionFlow.snapshots[currentActionFlow.snapshots.length - 1];

			// If the DOM hash matches, reuse the previous snapshot's DOM data
			if (previousSnapshot.domSnapshotHash === domSnapshotHash) {
				previousSnapshotId = previousSnapshot.id;
				// We'll keep a reference to the previous snapshot but won't duplicate the DOM data
				domSnapshot = ''; // Empty string indicates we're referencing a previous snapshot
			}
		}

		// Capture viewport dimensions
		const viewport = {
			width: window.innerWidth,
			height: window.innerHeight,
		};

		const newSnapshot: DOMSnapshot = {
			id: uuidv4(),
			timestamp: Date.now(),
			domSnapshot,
			domSnapshotHash,
			previousSnapshotId,
			interactionType,
			targetElement: targetElementSnapshot as DOMElement,
			interactionMetadata: enhancedMetadata,
			viewport,
		};

		let updatedActionFlow: ActionFlow;

		if (replaceLastSnapshot) {
			updatedActionFlow = {
				...currentActionFlow,
				snapshots: [...currentActionFlow.snapshots.slice(0, -1), newSnapshot],
			};
		} else {
			updatedActionFlow = {
				...currentActionFlow,
				snapshots: [...currentActionFlow.snapshots, newSnapshot],
			};
		}

		set({ currentActionFlow: updatedActionFlow });
		return newSnapshot;
	},

	// Clear the current action flow
	clearCurrentActionFlow: () => {
		set({
			currentActionFlow: null,
			isRecording: false,
		});
	},

	// Delete a saved action flow
	deleteActionFlow: (actionFlowId) => {
		set((state) => ({
			savedActionFlows: state.savedActionFlows.filter(
				(actionFlow) => actionFlow.id !== actionFlowId
			),
		}));
	},

	// Rename an action flow
	renameActionFlow: (actionFlowId, name) => {
		set((state) => ({
			savedActionFlows: state.savedActionFlows.map((actionFlow) =>
				actionFlow.id === actionFlowId ? { ...actionFlow, name } : actionFlow
			),
		}));
	},

	// Update an action flow's description
	updateActionFlowDescription: (actionFlowId, description) => {
		set((state) => ({
			savedActionFlows: state.savedActionFlows.map((actionFlow) =>
				actionFlow.id === actionFlowId
					? { ...actionFlow, description }
					: actionFlow
			),
		}));
	},

	// Export an action flow as JSON string
	exportActionFlow: (actionFlowId) => {
		const { savedActionFlows } = get();
		const actionFlow = savedActionFlows.find(
			(flow) => flow.id === actionFlowId
		);

		if (!actionFlow) {
			throw new Error(`Action flow with ID ${actionFlowId} not found`);
		}

		return JSON.stringify(actionFlow);
	},

	// Import an action flow from JSON string
	importActionFlow: (actionFlowData) => {
		try {
			const actionFlow = JSON.parse(actionFlowData) as ActionFlow;

			// Validate the action flow data
			if (
				!actionFlow.id ||
				!actionFlow.startTime ||
				!Array.isArray(actionFlow.snapshots)
			) {
				throw new Error('Invalid action flow data format');
			}

			// Add to saved action flows, ensuring we don't have duplicates
			set((state) => ({
				savedActionFlows: [
					...state.savedActionFlows.filter((flow) => flow.id !== actionFlow.id),
					actionFlow,
				],
			}));

			return true;
		} catch (error) {
			console.error('Failed to import action flow:', error);
			return false;
		}
	},
});
